import React, {useCallback, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import {useSelector} from "react-redux";

import ProductDetail from "../../../routes/catalog/product/edit/detail/product-details";
import ProductGallery from "../../../routes/catalog/product/edit/media/product-gallery";
import ProductDimension from "../../../routes/catalog/product/edit/dimension/product-dimension";
import ProductTranslation from "../../../routes/catalog/product/edit/translation/product-translation";
import ProductSupplier from "../../../routes/catalog/product/edit/supplier/product-supplier";
import ProductMedia from "../../../routes/catalog/product/edit/media/product-media";
import ProductLog from "../../../routes/catalog/product/edit/log/product-log";
import ProductAlternative from "../../../routes/catalog/product/edit/alternative/product-alternative";
import PriceHistory from "../../../routes/catalog/product/edit/pricing/price-history";
import {ProductEditProvider} from "../../../routes/catalog/product/edit/product-edit-context";
import LoaderWrapper from "../../../components/common/loader/loader-wrapper";
import {getProduct} from "../../../api/catalog";
import {getCategories} from "../../../api/category";
import {getSelectParams} from "../../../utils/converter";
import {
    permissionGroup,
    permissionSpecific,
    permissionType
} from "../../../constants/permissions";
import {hasPermissionFor} from "../../../components/partials/restricted/base-permission-wrapper";
import {
    DocumentTextIcon,
    PaperClipIcon,
    PhotographIcon,
    PresentationChartLineIcon,
    QrcodeIcon,
    ShoppingBagIcon,
    TemplateIcon
} from "@heroicons/react/outline";
import IntegrationIcon from "../../../resources/icons/IntegrationIcon";
import BoxIcon from "../../../resources/icons/BoxIcon";
import SlideTopPanel from "../../../components/layout/slide-top-panel";

const PRODUCT_EDIT_PANEL_Z_INDEX_CLASS = "z-[160]";
const PRODUCT_EDIT_PANEL_OVERLAY_Z_INDEX_CLASS = "z-[0]";

const TABS = {
    DETAIL: "detail",
    GALLERY: "gallery",
    PRICING: "pricing",
    SUPPLIERS: "suppliers",
    DIMENSIONS: "dimensions",
    MEDIA: "media",
    ALTERNATIVE: "alternative",
    TRANSLATION: "translation",
    LOG: "log",
};

const TAB_COMPONENTS = {
    [TABS.DETAIL]: ProductDetail,
    [TABS.GALLERY]: ProductGallery,
    [TABS.PRICING]: PriceHistory,
    [TABS.SUPPLIERS]: ProductSupplier,
    [TABS.DIMENSIONS]: ProductDimension,
    [TABS.MEDIA]: ProductMedia,
    [TABS.ALTERNATIVE]: ProductAlternative,
    [TABS.TRANSLATION]: ProductTranslation,
    [TABS.LOG]: ProductLog,
};

const getTranslate = (t) => (
    typeof t === "function"
        ? t
        : (key, options) => options?.defaultValue ?? key
);

const buildFallbackLocation = () => {
    if (typeof window === "undefined") {
        return {pathname: "", search: "", hash: ""};
    }
    return {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
    };
};

const DocumentProductEditPanel = ({
    isOpen,
    productId,
    companyId,
    onClose,
    onProductUpdated,
    t,
}) => {
    const translate = useMemo(() => getTranslate(t), [t]);
    const {permissions} = useSelector((state) => state.userCompany);

    const [productDetail, setProductDetail] = useState(null);
    const [isLoadingProduct, setLoadingProduct] = useState(false);
    const [isLoadingCategories, setLoadingCategories] = useState(false);
    const [categories, setCategories] = useState([]);
    const [needRefresh, setNeedRefresh] = useState(false);
    const [shouldSyncParent, setShouldSyncParent] = useState(false);
    const [isActiveSaveCheck, setActiveSaveCheck] = useState(false);
    const [isConfirmSave, setIsConfirmSave] = useState(false);
    const [activeTab, setActiveTab] = useState(null);

    const loadProduct = useCallback(async ({signal = null, notifyOnError = true, shouldUpdateParent = false} = {}) => {
        if (!productId || !companyId) {
            return null;
        }
        try {
            setLoadingProduct(true);
            const response = await getProduct({company_id: companyId, product_id: productId, signal});
            setProductDetail(response);
            if (shouldUpdateParent && typeof onProductUpdated === "function") {
                onProductUpdated(response);
            }
            return response;
        } catch (error) {
            if (signal?.aborted) {
                return null;
            }
            if (notifyOnError) {
                toast.error(translate("app.products.load_error", {defaultValue: "Impossibile aggiornare il prodotto"}));
            }
            return null;
        } finally {
            if (!signal?.aborted) {
                setLoadingProduct(false);
            }
        }
    }, [companyId, onProductUpdated, productId, translate]);

    const loadCategories = useCallback(async ({signal = null} = {}) => {
        if (!companyId) {
            return;
        }
        try {
            setLoadingCategories(true);
            const response = await getCategories({company_id: companyId, signal});
            setCategories(getSelectParams(response.categories, "name"));
        } catch (error) {
            if (signal?.aborted) {
                return;
            }
            setCategories([]);
        } finally {
            if (!signal?.aborted) {
                setLoadingCategories(false);
            }
        }
    }, [companyId]);

    useEffect(() => {
        if (!productId || !companyId) {
            setProductDetail(null);
            return;
        }
        const controller = new AbortController();
        loadProduct({signal: controller.signal});
        return () => controller.abort();
    }, [companyId, loadProduct, productId]);

    useEffect(() => {
        if (!companyId) {
            setCategories([]);
            return;
        }
        const controller = new AbortController();
        loadCategories({signal: controller.signal});
        return () => controller.abort();
    }, [companyId, loadCategories]);

    useEffect(() => {
        if (needRefresh) {
            loadProduct({notifyOnError: false});
            setNeedRefresh(false);
        }
    }, [loadProduct, needRefresh]);

    const handleSetNeedRefresh = useCallback((value) => {
        setNeedRefresh((previous) => {
            const nextValue = typeof value === "function" ? value(previous) : value;
            if (nextValue) {
                setShouldSyncParent(true);
            }
            return nextValue;
        });
    }, [setNeedRefresh, setShouldSyncParent]);

    useEffect(() => {
        setActiveSaveCheck(false);
        setIsConfirmSave(false);
    }, [productId]);

    const canViewProduct = hasPermissionFor(permissions, permissionGroup.PRODUCT, permissionType.VIEW);
    const canViewProductRate = hasPermissionFor(permissions, permissionGroup.PRODUCT_RATE, permissionType.LIST);
    const canViewProductSupplier = hasPermissionFor(permissions, permissionGroup.PRODUCT_SUPPLIER, permissionType.LIST);
    const canViewProductPackages = hasPermissionFor(permissions, permissionGroup.PRODUCT_PACKAGE, permissionType.LIST);
    const canViewProductAttachments = hasPermissionFor(permissions, permissionGroup.PRODUCT_ATTACHMENT, permissionType.LIST);
    const canViewProductTranslations = hasPermissionFor(permissions, permissionGroup.PRODUCT_TRANSLATION, permissionType.LIST);
    const canViewProductLog = hasPermissionFor(permissions, permissionGroup.PRODUCT, permissionSpecific.PRODUCT_LOG);
    const canViewAlternative = hasPermissionFor(permissions, permissionGroup.PRODUCT, permissionSpecific.PRODUCT_ALTERNATIVE);

    const tabs = useMemo(() => {
        if (!productId) {
            return [];
        }
        return [
            canViewProduct && {
                key: TABS.DETAIL,
                label: translate("app.products.info", {defaultValue: "Informazioni"}),
                icon: ShoppingBagIcon,
            },
            canViewProduct && {
                key: TABS.GALLERY,
                label: translate("app.common.gallery", {defaultValue: "Galleria"}),
                icon: PhotographIcon,
            },
            canViewProductRate && {
                key: TABS.PRICING,
                label: translate("app.products.pricing_title", {defaultValue: "Prezzi"}),
                icon: PresentationChartLineIcon,
            },
            canViewProductSupplier && {
                key: TABS.SUPPLIERS,
                label: translate("app.catalog.supplier", {defaultValue: "Fornitori"}),
                icon: QrcodeIcon,
            },
            canViewProductPackages && {
                key: TABS.DIMENSIONS,
                label: translate("app.products.package_title", {defaultValue: "Dimensioni"}),
                icon: BoxIcon,
            },
            canViewProductAttachments && {
                key: TABS.MEDIA,
                label: translate("app.catalog.attachments", {defaultValue: "Allegati"}),
                icon: PaperClipIcon,
            },
            canViewAlternative && {
                key: TABS.ALTERNATIVE,
                label: translate("app.catalog.alternative", {defaultValue: "Alternativi"}),
                icon: TemplateIcon,
            },
            canViewProductTranslations && {
                key: TABS.TRANSLATION,
                label: translate("app.products.translation", {defaultValue: "Traduzioni"}),
                icon: IntegrationIcon,
            },
            canViewProductLog && {
                key: TABS.LOG,
                label: translate("app.products.log", {defaultValue: "Log"}),
                icon: DocumentTextIcon,
            },
        ].filter(Boolean);
    }, [canViewAlternative, canViewProduct, canViewProductAttachments, canViewProductLog, canViewProductPackages, canViewProductRate, canViewProductSupplier, canViewProductTranslations, productId, translate]);

    useEffect(() => {
        if (tabs.length === 0) {
            setActiveTab(null);
            return;
        }
        if (!activeTab || !tabs.some((tab) => tab.key === activeTab)) {
            setActiveTab(tabs[0].key);
        }
    }, [activeTab, tabs]);

    const ActiveComponent = useMemo(() => {
        if (!activeTab) {
            return null;
        }
        return TAB_COMPONENTS[activeTab] ?? null;
    }, [activeTab]);

    const fallbackLocation = useMemo(() => buildFallbackLocation(), []);

    const contextValue = useMemo(() => ({
        productId,
        companyId,
        productDetail,
        navigation: tabs,
        categories,
        isLoadingCategories,
        setProductDetail,
        setNeedRefresh: handleSetNeedRefresh,
        isActiveSaveCheck,
        setActiveSaveCheck,
        isConfirmSave,
        onConfirmUpdate: () => {
            setActiveSaveCheck(false);
            setIsConfirmSave(false);
            loadProduct({notifyOnError: false});
        },
        location: fallbackLocation,
        navigate: () => {},
        hideBackLink: true,
        panelZIndexClass: PRODUCT_EDIT_PANEL_Z_INDEX_CLASS,
        overlayZIndexClass: PRODUCT_EDIT_PANEL_OVERLAY_Z_INDEX_CLASS,
    }), [
        categories,
        companyId,
        fallbackLocation,
        handleSetNeedRefresh,
        isActiveSaveCheck,
        isConfirmSave,
        isLoadingCategories,
        loadProduct,
        productDetail,
        productId,
        tabs,
    ]);

    const handleSetClean = useCallback(async () => {
        if (!shouldSyncParent) {
            return;
        }
        await loadProduct({notifyOnError: false, shouldUpdateParent: true});
        setShouldSyncParent(false);
    }, [loadProduct, shouldSyncParent]);

    const handleSetIsOpen = useCallback((nextOpen) => {
        if (!nextOpen && typeof onClose === "function") {
            onClose();
        }
    }, [onClose]);

    return (
        <SlideTopPanel
            title={translate("app.products.edit_product", {defaultValue: "Modifica prodotto"})}
            bg="bg-white"
            size="max-w-6xl"
            paddingStyle="p-0"
            hasDivider={false}
            activeOverflow
            isOpen={isOpen}
            setIsOpen={handleSetIsOpen}
            setClean={handleSetClean}
        >
            {productId ? (
                <ProductEditProvider value={contextValue}>
                    <div className="relative flex h-full flex-col bg-gray-50">
                        {tabs.length > 0 && (
                            <div className="sticky top-0 z-30 border-b border-gray-200 bg-white px-6 py-4 shadow-sm">
                                <nav className="flex items-center gap-2 overflow-x-auto pb-1" role="tablist">
                                    {tabs.map(({key, label, icon: Icon}) => (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => setActiveTab(key)}
                                            role="tab"
                                            aria-selected={activeTab === key}
                                            className={`group inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 ${
                                                activeTab === key
                                                    ? "border-blue-500 bg-blue-50 text-blue-700 shadow-sm"
                                                    : "border-transparent bg-transparent text-gray-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                                            }`}
                                        >
                                            {Icon && (
                                                <Icon
                                                    className={`h-4 w-4 ${
                                                        activeTab === key ? "text-blue-600" : "text-gray-400 group-hover:text-blue-500"
                                                    }`}
                                                    aria-hidden="true"
                                                />
                                            )}
                                            {label}
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        )}
                        <div className="relative flex-1 overflow-y-auto">
                            <div className="px-6 py-6">
                                <LoaderWrapper isLoading={isLoadingProduct}>
                                    {ActiveComponent ? (
                                        <ActiveComponent />
                                    ) : (
                                        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-500 shadow-sm">
                                            {translate("app.catalog.no_sections_available", {defaultValue: "Nessuna sezione disponibile."})}
                                        </div>
                                    )}
                                </LoaderWrapper>
                            </div>
                        </div>
                    </div>
                </ProductEditProvider>
            ) : (
                <div className="p-6 text-sm text-gray-500">
                    {translate("app.documents.select_product_to_edit", {defaultValue: "Seleziona un prodotto da modificare."})}
                </div>
            )}
        </SlideTopPanel>
    );
};

DocumentProductEditPanel.propTypes = {
    isOpen: PropTypes.bool,
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onClose: PropTypes.func,
    onProductUpdated: PropTypes.func,
    t: PropTypes.func,
};

DocumentProductEditPanel.defaultProps = {
    isOpen: false,
    productId: null,
    companyId: null,
    onClose: null,
    onProductUpdated: null,
    t: null,
};

export default DocumentProductEditPanel;
